/**
 * FunctionSignature — a structured API reference block for OpenSCAD functions.
 *
 * Renders a card containing one or more call signatures (overloads), a parameter
 * table (name, type, default, description), an optional return description, an
 * optional static code example, and an optional note callout.
 *
 * Usage in MDX (no import needed — registered globally in BlogMDXProvider):
 *
 *   <FunctionSignature
 *     name="linear_extrude"
 *     description="Extrudes a 2D shape into a 3D solid along the Z axis."
 *     signatures={[
 *       { syntax: "linear_extrude(height, center, convexity, twist, slices, scale)" },
 *     ]}
 *     params={[
 *       { name: "height", type: "number", default: "100", description: "Extrusion height in mm." },
 *       { name: "center", type: "boolean", default: "false", description: "Centre on Z axis when true." },
 *     ]}
 *     returns="A 3D solid."
 *     example={`linear_extrude(height = 10)\n  circle(r = 5);`}
 *     note="height must be positive and non-zero."
 *   />
 */

import { cn } from '@/lib/utils';
import { Tip } from './Tip';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FunctionParameter {
  /** Parameter name as it appears in the OpenSCAD source, e.g. "height" */
  name: string;
  /**
   * Data type, e.g. "number", "boolean", "vector", "string", "range", "undef"
   * Displayed as a small monospace badge.
   */
  type: string;
  /**
   * Default value as a string, e.g. "100", "false", "undef".
   * Omit (or pass undefined) when there is no default.
   */
  default?: string;
  /** One or two sentence description of what the parameter controls. */
  description: string;
}

export interface FunctionSignatureOverload {
  /**
   * Full call syntax shown verbatim, e.g.
   * "linear_extrude(height, center, convexity, twist, slices, scale)"
   */
  syntax: string;
  /**
   * Optional short note that distinguishes this overload from others,
   * e.g. "used when twist ≠ 0".
   */
  note?: string;
}

export interface FunctionSignatureProps {
  /** The OpenSCAD function name, e.g. "linear_extrude" */
  name: string;
  /** One-sentence description of what the function does */
  description: string;
  /**
   * One or more call signatures (overloads). Pass a single-element array
   * when there is only one signature.
   */
  signatures: FunctionSignatureOverload[];
  /**
   * All accepted parameters, documented once even if shared across overloads.
   * Order should match the canonical OpenSCAD documentation order.
   */
  params: FunctionParameter[];
  /**
   * Description of what the function produces / returns,
   * e.g. "A 3D solid object." Omit for void/statement-level functions.
   */
  returns?: string;
  /**
   * Optional static OpenSCAD code snippet displayed in a syntax-highlighted
   * block below the parameter table. Use backtick template literals in JSX
   * to preserve newlines.
   */
  example?: string;
  /**
   * Optional extra note rendered as a <Tip variant="note"> below the table
   * (and above the example if both are present).
   */
  note?: string;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Small pill badge for a parameter type. */
function TypeBadge({ type }: { type: string }) {
  return (
    <span className="inline-block whitespace-nowrap rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-xs leading-none text-muted-foreground">
      {type}
    </span>
  );
}

// ---------------------------------------------------------------------------
// OpenSCAD tokenizer (shared by signature coloring and example highlighting)
// ---------------------------------------------------------------------------

/** Token categories produced by tokenizeScad. */
type ScadTokenKind =
  | 'keyword' // built-in functions, module, function, if, else, for, true, false, undef
  | 'comment' // // … and /* … */
  | 'string' // "…"
  | 'number' // integer, float, hex
  | 'identifier' // user variable / module name
  | 'operator' // = > < ! + - * / % == …
  | 'punctuation' // ( ) { } [ ] , ; .
  | 'param' // named argument (word immediately before =)
  | 'text'; // plain whitespace / newlines

interface ScadToken {
  kind: ScadTokenKind;
  value: string;
}

const SCAD_KEYWORDS = new Set([
  'module',
  'function',
  'if',
  'else',
  'for',
  'let',
  'each',
  'union',
  'difference',
  'intersection',
  'hull',
  'minkowski',
  'translate',
  'rotate',
  'scale',
  'resize',
  'mirror',
  'multmatrix',
  'color',
  'material',
  'material_def',
  'cylinder',
  'cube',
  'sphere',
  'circle',
  'square',
  'polygon',
  'polyhedron',
  'text',
  'surface',
  'import',
  'echo',
  'use',
  'include',
  'render',
  'projection',
  'linear_extrude',
  'rotate_extrude',
  'offset',
  'true',
  'false',
  'undef',
]);

const SCAD_SPECIAL_VARS = new Set([
  '$fn',
  '$fa',
  '$fs',
  '$t',
  '$vpr',
  '$vpt',
  '$vpd',
]);

/**
 * Tokenises a block of OpenSCAD source into typed tokens.
 * Line comments (//) and block comments (/* *\/) are included as single tokens.
 */
function tokenizeScad(src: string): ScadToken[] {
  const tokens: ScadToken[] = [];
  let i = 0;

  while (i < src.length) {
    // ── Block comment ──────────────────────────────────────────────────────
    if (src[i] === '/' && src[i + 1] === '*') {
      const end = src.indexOf('*/', i + 2);
      const value = end === -1 ? src.slice(i) : src.slice(i, end + 2);
      tokens.push({ kind: 'comment', value });
      i += value.length;
      continue;
    }

    // ── Line comment ───────────────────────────────────────────────────────
    if (src[i] === '/' && src[i + 1] === '/') {
      let end = i + 2;
      while (end < src.length && src[end] !== '\n') end++;
      tokens.push({ kind: 'comment', value: src.slice(i, end) });
      i = end;
      continue;
    }

    // ── String literal ─────────────────────────────────────────────────────
    if (src[i] === '"') {
      let end = i + 1;
      while (end < src.length && src[end] !== '"') {
        if (src[end] === '\\') end++; // skip escaped char
        end++;
      }
      if (end < src.length) end++; // consume closing "
      tokens.push({ kind: 'string', value: src.slice(i, end) });
      i = end;
      continue;
    }

    // ── Whitespace / newlines ──────────────────────────────────────────────
    if (/\s/.test(src[i])) {
      let end = i + 1;
      while (end < src.length && /\s/.test(src[end])) end++;
      tokens.push({ kind: 'text', value: src.slice(i, end) });
      i = end;
      continue;
    }

    // ── Hex number ────────────────────────────────────────────────────────
    if (src[i] === '0' && (src[i + 1] === 'x' || src[i + 1] === 'X')) {
      let end = i + 2;
      while (end < src.length && /[0-9a-fA-F]/.test(src[end])) end++;
      tokens.push({ kind: 'number', value: src.slice(i, end) });
      i = end;
      continue;
    }

    // ── Float / integer ───────────────────────────────────────────────────
    if (
      /[0-9]/.test(src[i]) ||
      (src[i] === '.' && /[0-9]/.test(src[i + 1] ?? ''))
    ) {
      let end = i;
      while (end < src.length && /[0-9]/.test(src[end])) end++;
      if (src[end] === '.') {
        end++;
        while (end < src.length && /[0-9]/.test(src[end])) end++;
      }
      if (src[end] === 'e' || src[end] === 'E') {
        end++;
        if (src[end] === '+' || src[end] === '-') end++;
        while (end < src.length && /[0-9]/.test(src[end])) end++;
      }
      tokens.push({ kind: 'number', value: src.slice(i, end) });
      i = end;
      continue;
    }

    // ── Identifier, keyword, special variable, or named param ─────────────
    if (/[a-zA-Z_$]/.test(src[i])) {
      let end = i + 1;
      while (end < src.length && /[\w$]/.test(src[end])) end++;
      const word = src.slice(i, end);

      // Look ahead past whitespace for '=' to detect named arguments.
      let j = end;
      while (j < src.length && src[j] === ' ') j++;
      const isNamedArg = src[j] === '=';

      let kind: ScadTokenKind;
      if (SCAD_KEYWORDS.has(word)) {
        kind = 'keyword';
      } else if (SCAD_SPECIAL_VARS.has(word)) {
        kind = 'keyword';
      } else if (isNamedArg) {
        kind = 'param';
      } else {
        kind = 'identifier';
      }

      tokens.push({ kind, value: word });
      i = end;
      continue;
    }

    // ── Special variable starting with $ (e.g. $fn, $fa) ─────────────────
    if (src[i] === '$') {
      let end = i + 1;
      while (end < src.length && /[\w]/.test(src[end])) end++;
      const word = src.slice(i, end);
      tokens.push({
        kind: SCAD_SPECIAL_VARS.has(word) ? 'keyword' : 'identifier',
        value: word,
      });
      i = end;
      continue;
    }

    // ── Punctuation ───────────────────────────────────────────────────────
    if ('(){}[];,'.includes(src[i])) {
      tokens.push({ kind: 'punctuation', value: src[i] });
      i++;
      continue;
    }

    // ── Operators ─────────────────────────────────────────────────────────
    if ('=<>!&|+-*/%^~?:'.includes(src[i])) {
      let end = i + 1;
      // Grab two-character operators (==, !=, <=, >=, &&, ||)
      if (end < src.length && '=&|'.includes(src[end]) && src[i] !== '=') end++;
      else if (src[i] === '=' && src[end] === '=') end++;
      tokens.push({ kind: 'operator', value: src.slice(i, end) });
      i = end;
      continue;
    }

    // ── Fallback: emit single character as text ────────────────────────────
    tokens.push({ kind: 'text', value: src[i] });
    i++;
  }

  return tokens;
}

/** Maps a ScadTokenKind to a Tailwind text-color class (dark-mode-friendly). */
function scadTokenClass(kind: ScadTokenKind): string {
  switch (kind) {
    case 'keyword':
      return 'text-sky-400 dark:text-sky-300';
    case 'comment':
      return 'text-zinc-500 dark:text-zinc-500 italic';
    case 'string':
      return 'text-emerald-400 dark:text-emerald-300';
    case 'number':
      return 'text-emerald-400 dark:text-emerald-300';
    case 'param':
      return 'text-amber-400 dark:text-amber-300';
    case 'identifier':
      return 'text-zinc-200 dark:text-zinc-200';
    case 'operator':
      return 'text-zinc-400 dark:text-zinc-400';
    case 'punctuation':
      return 'text-zinc-400 dark:text-zinc-400';
    default:
      return '';
  }
}

/** Renders a block of OpenSCAD source with lightweight syntax highlighting. */
function HighlightedScad({ code }: { code: string }) {
  const tokens = tokenizeScad(code);
  return (
    <>
      {tokens.map((tok, idx) => {
        const cls = scadTokenClass(tok.kind);
        return cls ? (
          <span key={idx} className={cls}>
            {tok.value}
          </span>
        ) : (
          tok.value
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Signature-specific coloring (wraps the shared tokenizer)
// ---------------------------------------------------------------------------

/**
 * Very lightweight OpenSCAD syntax coloring applied to a signature string.
 * Highlights the function name (up to the first `(`), parameter names
 * (word before `=` or `,`/`)`) and punctuation, without pulling in Monaco.
 *
 * Returns an array of React nodes with `<span>` wrappers for colored tokens.
 */
function ColorizedSignature({ syntax }: { syntax: string }) {
  // Split on the first '(' to separate function name from arg list.
  const parenIdx = syntax.indexOf('(');
  if (parenIdx === -1) {
    return <span className="text-sky-400 dark:text-sky-300">{syntax}</span>;
  }

  const funcName = syntax.slice(0, parenIdx);
  const rest = syntax.slice(parenIdx); // includes '(' ... ')'

  // Tokenise the argument list: identify param names (before '=' or ','/').
  const tokens: React.ReactNode[] = [];
  let i = 0;
  while (i < rest.length) {
    const ch = rest[i];

    // Punctuation: ( ) , =
    if (ch === '(' || ch === ')' || ch === ',' || ch === '=') {
      tokens.push(
        <span key={i} className="text-zinc-400 dark:text-zinc-500">
          {ch}
        </span>,
      );
      i++;
      continue;
    }

    // Whitespace — preserve as-is
    if (ch === ' ' || ch === '\t') {
      tokens.push(ch);
      i++;
      continue;
    }

    // Collect a word token (identifier or literal)
    let word = '';
    const start = i;
    while (i < rest.length && /[\w.$]/.test(rest[i])) {
      word += rest[i++];
    }

    if (word.length === 0) {
      // Non-matched single character (e.g. '[', ']', '"')
      tokens.push(
        <span key={start} className="text-zinc-300">
          {rest[start]}
        </span>,
      );
      i = start + 1;
      continue;
    }

    // Look ahead past optional whitespace for '=' to decide if this is a
    // param name (param = value) or a value literal.
    let j = i;
    while (j < rest.length && rest[j] === ' ') j++;
    const isParamName = rest[j] === '=';
    // Also treat as param name if it's followed by ',' or ')' directly
    // (positional param — no default shown in signature).
    const isPositional =
      !isParamName && (rest[j] === ',' || rest[j] === ')' || j >= rest.length);

    if (isParamName || isPositional) {
      tokens.push(
        <span key={start} className="text-emerald-400 dark:text-emerald-300">
          {word}
        </span>,
      );
    } else {
      // Default value literal
      tokens.push(
        <span key={start} className="text-amber-400 dark:text-amber-300">
          {word}
        </span>,
      );
    }
  }

  return (
    <>
      <span className="text-sky-400 dark:text-sky-300">{funcName}</span>
      {tokens}
    </>
  );
}

/** Renders a single overload row in the signature panel. */
function SignatureRow({
  overload,
  index,
  total,
}: {
  overload: FunctionSignatureOverload;
  index: number;
  total: number;
}) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      {total > 1 && (
        <span className="shrink-0 select-none font-mono text-xs text-zinc-500">
          {index + 1}.
        </span>
      )}
      <code className="min-w-0 break-all font-mono text-sm leading-relaxed">
        <ColorizedSignature syntax={overload.syntax} />
      </code>
      {overload.note && (
        <span className="text-xs italic text-zinc-500 dark:text-zinc-400">
          — {overload.note}
        </span>
      )}
    </div>
  );
}

/** Parameter table row. */
function ParamRow({ param }: { param: FunctionParameter }) {
  return (
    <tr className="border-b border-border last:border-0">
      {/* Name */}
      <td className="py-2.5 pl-4 pr-4 align-top">
        <code className="font-mono text-sm font-semibold text-foreground">
          {param.name}
        </code>
      </td>
      {/* Type */}
      <td className="py-2.5 pr-4 align-top">
        <TypeBadge type={param.type} />
      </td>
      {/* Default */}
      <td className="py-2.5 pr-4 align-top">
        {param.default !== undefined ? (
          <code className="font-mono text-sm text-muted-foreground">
            {param.default}
          </code>
        ) : (
          <span
            className="text-sm text-zinc-400 dark:text-zinc-600"
            aria-label="no default"
          >
            —
          </span>
        )}
      </td>
      {/* Description */}
      <td className="py-2.5 align-top text-sm leading-relaxed text-foreground">
        {param.description}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function FunctionSignature({
  name,
  description,
  signatures,
  params,
  returns,
  example,
  note,
}: FunctionSignatureProps) {
  return (
    <div className="not-prose my-8 overflow-hidden rounded-lg border border-border bg-card">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border px-4 py-3">
        <code className="font-mono text-base font-bold text-foreground">
          {name}
        </code>
        <span className="text-sm text-muted-foreground">{description}</span>
      </div>

      {/* ── Signature block ─────────────────────────────────────────────── */}
      <div className="border-b border-border bg-zinc-950 px-4 py-3 dark:bg-zinc-900">
        <div className="flex flex-col gap-2">
          {signatures.map((sig, i) => (
            <SignatureRow
              key={i}
              overload={sig}
              index={i}
              total={signatures.length}
            />
          ))}
        </div>
      </div>

      {/* ── Parameter table ─────────────────────────────────────────────── */}
      {params.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Parameter
                </th>
                <th className="px-0 py-2 pr-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Type
                </th>
                <th className="py-2 pr-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Default
                </th>
                <th className="py-2 pr-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Description
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {params.map((p) => (
                <ParamRow key={p.name} param={p} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Returns ─────────────────────────────────────────────────────── */}
      {returns && (
        <div
          className={cn(
            'flex items-baseline gap-2 px-4 py-2.5 text-sm',
            params.length > 0 && 'border-t border-border',
          )}
        >
          <span className="shrink-0 font-semibold text-muted-foreground">
            Returns:
          </span>
          <span className="text-foreground">{returns}</span>
        </div>
      )}

      {/* ── Note callout ────────────────────────────────────────────────── */}
      {note && (
        <div className="border-t border-border px-4 pb-1 pt-2">
          <Tip variant="note">{note}</Tip>
        </div>
      )}

      {/* ── Example snippet ─────────────────────────────────────────────── */}
      {example && (
        <div className="border-t border-border">
          <div className="flex items-center justify-between border-b border-border bg-muted/40 px-4 py-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Example
            </span>
            <span className="font-mono text-xs text-zinc-500">scad</span>
          </div>
          <pre className="overflow-x-auto bg-zinc-950 px-4 py-3 dark:bg-zinc-900">
            <code className="font-mono text-sm leading-relaxed text-zinc-200">
              <HighlightedScad code={example} />
            </code>
          </pre>
        </div>
      )}
    </div>
  );
}
